import moment from 'moment-timezone';
import { FALLBACK_TIME_ZONE } from '@core/helpers/display-time.helper';

// Zone of a day chart whose caller names none.
export const DEFAULT_TIME_ZONE = FALLBACK_TIME_ZONE;

// Invisible prefix that tells a quarter-hour label (start time) apart from an hour label (end time).
export const QUARTER_LABEL_MARKER = '\u200b';

export interface DstSuffixes {
  summerTime: string;
  winterTime: string;
}

/**
 * The fixed positions of a day chart (24 hours or 96 quarters) and where the backend intervals of the
 * day land on them. The backend returns one interval per real elapsed hour or quarter, so a clock-change
 * day has 23/25 hours or 92/100 quarters; the axis never changes.
 */
export interface DaySlots {
  labels: string[];
  /** Position of every interval. */
  positions: number[];
  /** Intervals that repeat a position already taken: the winter-time hour (or quarters) of the 25-hour day. */
  repeated: number[];
  /** Positions without an interval: the hour (or quarters) the 23-hour day skips. */
  skipped: number[];
  /** One label per interval for tables, the repeated hour told apart by a summer/winter-time suffix. */
  intervalLabels: string[];
}

export interface ClockChange {
  zone: string;
  transition: 'forward' | 'back';
  hours: 23 | 25;
}

/** The clock change on the local day of `referenceDate` in `timeZone`, from the length between its midnights. */
export function clockChangeOf(
  referenceDate: moment.MomentInput,
  timeZone: string = DEFAULT_TIME_ZONE,
): ClockChange | null {
  const midnight = moment.tz(referenceDate, timeZone).startOf('day');
  const hours = midnight.clone().add(1, 'day').diff(midnight, 'hours');
  if (hours === 24) return null;
  return {
    zone: timeZone,
    transition: hours < 24 ? 'forward' : 'back',
    hours: hours < 24 ? 23 : 25,
  };
}

// Places every interval on the position its wall-clock start falls in. An interval sharing a position
// with an earlier one is the winter-time hour (or quarter) of the 25-hour day; both are told apart in
// their table label by a summer/winter-time suffix on the position's own label.
function placeIntervals(
  starts: moment.Moment[],
  labels: string[],
  positionOf: (start: moment.Moment) => number,
  suffixes: DstSuffixes,
): DaySlots {
  const positions = starts.map(positionOf);
  const firstAt = new Map<number, number>();
  const repeated: number[] = [];
  positions.forEach((position, index) => {
    if (firstAt.has(position)) repeated.push(index);
    else firstAt.set(position, index);
  });
  const repeatedPositions = new Set(repeated.map(index => positions[index]));
  const skipped = labels.map((_, position) => position).filter(position => !firstAt.has(position));
  const intervalLabels = starts.map((start, index) => {
    const label = labels[positions[index]];
    if (!repeatedPositions.has(positions[index])) return label;
    return `${label} ${start.isDST() ? suffixes.summerTime : suffixes.winterTime}`;
  });
  return { labels, positions, repeated, skipped, intervalLabels };
}

/**
 * Builds the 24 positions of a per-hour day chart, each labelled with the wall-clock time at which its
 * hour ends. `hours` counts the elapsed hours of the day from 1. An hour lands on the position of its
 * wall-clock start, so both 02:00-03:00 hours of the 25-hour day share the position labelled 3:00 and
 * the 23-hour day leaves that position empty.
 */
export function buildDayHourSlots(
  referenceDate: moment.MomentInput,
  hours: number[],
  suffixes: DstSuffixes,
  timeZone: string = DEFAULT_TIME_ZONE,
): DaySlots {
  const midnight = moment.tz(referenceDate, timeZone).startOf('day');
  const labels = Array.from({ length: 24 }, (_, position) => `${(position + 1) % 24}:00`);
  const starts = hours.map(hour => midnight.clone().add(hour - 1, 'hours'));
  return placeIntervals(starts, labels, start => start.hour(), suffixes);
}

/**
 * Builds the 96 positions of a per-quarter-hour day chart, each labelled with the wall-clock time at which
 * its quarter starts. `quarterHours` counts the elapsed quarters of the day from 0.
 */
export function buildDayQuarterSlots(
  referenceDate: moment.MomentInput,
  quarterHours: number[],
  suffixes: DstSuffixes,
  timeZone: string = DEFAULT_TIME_ZONE,
): DaySlots {
  const midnight = moment.tz(referenceDate, timeZone).startOf('day');
  const labels = Array.from(
    { length: 96 },
    (_, position) => `${Math.floor(position / 4)}:${String((position % 4) * 15).padStart(2, '0')}`,
  );
  const starts = quarterHours.map(quarter => midnight.clone().add(quarter * 15, 'minutes'));
  return placeIntervals(
    starts,
    labels,
    start => start.hour() * 4 + Math.floor(start.minute() / 15),
    suffixes,
  );
}

export interface FoldableDataset {
  label?: string;
  data: unknown[];
  stack?: string;
}

/**
 * Places the interval values of every dataset on the fixed positions of `slots`.
 *
 * On the 25-hour day every dataset keeps its summer-time value in the repeated position. A stackable
 * dataset (an hourly amount such as energy) also gets one bar there, built from two datasets that share a
 * stack: the summer-time value in the dataset's own colour and, on top of it, the winter-time value with a
 * suffixed label in a lighter colour. `barStyle` turns a copy of the dataset into such a bar part. A
 * dataset that is not stackable (an average such as a voltage, where adding two hours means nothing) and an
 * unlabelled one (a reference line) just lose the extra value. On the 23-hour day the skipped positions
 * stay empty; the caller marks them, see {@link dstMarkerData}.
 */
export function placeOnDaySlots<T extends FoldableDataset>(
  datasets: T[],
  slots: DaySlots,
  suffix: string,
  barStyle: (dataset: T, part: 'summer' | 'winter') => Partial<T>,
  stackable: (dataset: T) => boolean,
): T[] {
  const unchanged =
    slots.repeated.length === 0 &&
    slots.skipped.length === 0 &&
    slots.positions.every((position, index) => position === index);
  if (unchanged) return datasets;
  const repeated = new Set(slots.repeated);
  const winterAt = new Map(slots.repeated.map(index => [slots.positions[index], index]));
  return datasets.flatMap((dataset, i) => {
    const placed: unknown[] = slots.labels.map(() => null);
    dataset.data.forEach((value, index) => {
      if (index < slots.positions.length && !repeated.has(index)) {
        placed[slots.positions[index]] = value;
      }
    });
    if (winterAt.size === 0) return [{ ...dataset, data: placed }];
    // Every dataset gets its own stack id: once a scale is stacked, Chart.js otherwise stacks all
    // stack-less datasets of a type on top of each other.
    const own = { ...dataset, data: placed, stack: `hour-${i}` };
    if (!dataset.label || !stackable(dataset)) return [own];
    const part = (value: (position: number, index: number) => unknown) =>
      placed.map((_, position) =>
        winterAt.has(position) ? value(position, winterAt.get(position)!) : null,
      );
    const stack = `repeated-hour-${i}`;
    return [
      own,
      {
        ...dataset,
        ...barStyle(dataset, 'summer'),
        label: '',
        data: part(position => placed[position]),
        stack,
      },
      {
        ...dataset,
        ...barStyle(dataset, 'winter'),
        label: `${dataset.label} (${suffix})`,
        data: part((_, index) => dataset.data[index] ?? null),
        stack,
      },
    ];
  });
}

/** Values of the daylight saving marker: on the axis at every skipped position, empty elsewhere. */
export function dstMarkerData(slots: DaySlots): (number | null)[] {
  const skipped = new Set(slots.skipped);
  return slots.labels.map((_, position) => (skipped.has(position) ? 0 : null));
}

/** Tooltip title of an hour interval: the label of the previous interval up to this interval's own label. */
export function dayHourTooltipTitle(labels: unknown[], index: number): string {
  const start = index > 0 ? String(labels[index - 1]) : '0:00';
  return `${start} - ${String(labels[index])}`;
}

/**
 * Tooltip title of a day chart interval. An hour label is the time the hour ends; a quarter label (marked)
 * is the time the quarter starts and the quarter ends where the next one starts, which on a clock-change
 * day is not always 15 minutes of wall-clock time later.
 */
export function dayTooltipTitle(labels: unknown[], index: number): string {
  const label = String(labels[index]);
  if (!label.startsWith(QUARTER_LABEL_MARKER)) return dayHourTooltipTitle(labels, index);
  const end =
    index + 1 < labels.length
      ? String(labels[index + 1]).replace(QUARTER_LABEL_MARKER, '')
      : '0:00';
  return `${label} - ${end}`;
}
